<template>
  <div class="storage-buckets-container">
    <div class="storage-buckets-header">
      <h2>文件存储管理</h2>
      <el-button v-if="$canWrite()" type="primary" @click="openCreateDialog">创建存储桶</el-button>
    </div>
    
    <!-- 项目选择器 -->
    <el-card shadow="hover" class="project-selector-card" v-if="projects.length > 0">
      <div class="project-selector">
        <label>选择项目：</label>
        <el-select v-model="selectedProjectId" placeholder="请选择项目" style="width: 200px" @change="fetchBuckets">
          <el-option
            v-for="project in projects"
            :key="project.id"
            :label="project.name"
            :value="project.id"
          ></el-option>
        </el-select>
      </div>
    </el-card>
    
    <!-- 存储桶列表 -->
    <el-card shadow="hover" class="storage-buckets-card" v-if="selectedProjectId">
      <el-table :data="paginatedBuckets" style="width: 100%" @selection-change="handleSelectionChange">
        <el-table-column v-if="$canWrite()" type="selection" width="55"></el-table-column>
        <el-table-column prop="id" label="ID" width="80"></el-table-column>
        <el-table-column prop="name" label="存储桶名称" width="200"></el-table-column>
        <el-table-column prop="description" label="描述"></el-table-column>
        <el-table-column prop="status" label="状态" width="100">
          <template slot-scope="scope">
            <el-tag :type="scope.row.status === 1 ? 'success' : 'info'">
              {{ scope.row.status === 1 ? '启用' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="created_at" label="创建时间" width="240"></el-table-column>
        <el-table-column label="操作" width="240">
          <template slot-scope="scope">
            <el-button type="primary" size="small" @click="viewBucket(scope.row)">查看</el-button>
            <el-button v-if="$canWrite()" type="warning" size="small" @click="openEditDialog(scope.row)">编辑</el-button>
            <el-button v-if="$canWrite()" type="danger" size="small" @click="handleDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
      
      <!-- 分页固定在底部 -->
      <div class="pagination-footer">
        <div class="footer-left">
          <el-button
            v-if="$canWrite()"
            type="danger"
            size="small"
            :disabled="selectedBuckets.length === 0"
            @click="batchDelete"
          >
            批量删除 ({{ selectedBuckets.length }})
          </el-button>
        </div>
        
        <el-pagination
          @current-change="handlePageChange"
          :current-page="pagination.currentPage"
          :page-size="pagination.pageSize"
          layout="total, prev, pager, next, jumper"
          :total="totalBuckets"
        ></el-pagination>
      </div>
    </el-card>

    <!-- 创建存储桶对话框 -->
    <el-dialog title="创建存储桶" :visible.sync="createDialogVisible" width="500px">
      <el-form ref="createForm" :model="form" :rules="rules" label-position="top">
        <el-form-item label="存储桶名称" prop="name">
          <el-input v-model="form.name" placeholder="请输入存储桶名称"></el-input>
        </el-form-item>
        <el-form-item label="描述" prop="description">
          <el-input v-model="form.description" placeholder="请输入描述" type="textarea" rows="3"></el-input>
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="form.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">禁用</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="createDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleCreate" :loading="loading">创建</el-button>
      </div>
    </el-dialog>

    <!-- 编辑存储桶对话框 -->
    <el-dialog title="编辑存储桶" :visible.sync="editDialogVisible" width="500px">
      <el-form ref="editForm" :model="form" :rules="rules" label-position="top">
        <el-form-item label="存储桶名称" prop="name">
          <el-input v-model="form.name" placeholder="请输入存储桶名称"></el-input>
        </el-form-item>
        <el-form-item label="描述" prop="description">
          <el-input v-model="form.description" placeholder="请输入描述" type="textarea" rows="3"></el-input>
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="form.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">禁用</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="editDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleUpdate" :loading="loading">更新</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'StorageBucketList',
  data() {
    return {
      projects: [],
      selectedProjectId: null,
      buckets: [],
      selectedBuckets: [], // 选中的存储桶
      createDialogVisible: false,
      editDialogVisible: false,
      loading: false,
      // 分页
      pagination: {
        currentPage: 1,
        pageSize: 10
      },
      form: {
        id: null,
        name: '',
        description: '',
        status: 1
      },
      rules: {
        name: [
          { required: true, message: '请输入存储桶名称', trigger: 'blur' },
          { min: 2, message: '存储桶名称长度不能少于2个字符', trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    paginatedBuckets() {
      const start = (this.pagination.currentPage - 1) * this.pagination.pageSize;
      const end = start + this.pagination.pageSize;
      return this.buckets.slice(start, end);
    },
    totalBuckets() {
      return this.buckets.length;
    }
  },
  created() {
    const spaceId = this.$route.params.id;
    if (spaceId) {
      this.selectedProjectId = spaceId;
      this.fetchBuckets();
    }
  },
  methods: {
    async fetchProjects() {
      try {
        const projects = await this.$store.dispatch('fetchProjects')
        this.projects = projects
        if (projects.length > 0) {
          this.selectedProjectId = projects[0].id
          this.fetchBuckets()
        } else {
          this.$message.warning('暂无项目，请先创建项目')
        }
      } catch (error) {
        console.error('获取项目列表失败:', error)
        this.$message.error('获取项目列表失败: ' + (error.response?.data?.error || error.message))
      }
    },
    async fetchBuckets() {
      if (!this.selectedProjectId) return
      
      try {
        const response = await this.$axios.get(`/projects/${this.selectedProjectId}/buckets`)
        this.buckets = response.data || []
      } catch (error) {
        console.error('获取存储桶列表失败:', error)
        this.$message.error('获取存储桶列表失败: ' + (error.response?.data?.error || error.message))
      }
    },
    openCreateDialog() {
      this.form = {
        id: null,
        name: '',
        description: '',
        status: 1
      }
      this.createDialogVisible = true
    },
    openEditDialog(bucket) {
      this.form = JSON.parse(JSON.stringify(bucket))
      this.editDialogVisible = true
    },
    async handleCreate() {
      this.$refs.createForm.validate(async (valid) => {
        if (valid) {
          this.loading = true
          try {
            await this.$axios.post(`/projects/${this.selectedProjectId}/buckets`, this.form)
            this.$message.success('存储桶创建成功')
            this.createDialogVisible = false
            this.fetchBuckets()
          } catch (error) {
            this.$message.error('存储桶创建失败')
          } finally {
            this.loading = false
          }
        }
      })
    },
    async handleUpdate() {
      this.$refs.editForm.validate(async (valid) => {
        if (valid) {
          this.loading = true
          try {
            await this.$axios.put(`/projects/${this.selectedProjectId}/buckets/${this.form.id}`, this.form)
            this.$message.success('存储桶更新成功')
            this.editDialogVisible = false
            this.fetchBuckets()
          } catch (error) {
            this.$message.error('存储桶更新失败')
          } finally {
            this.loading = false
          }
        }
      })
    },
    async handleDelete(bucket) {
      this.$confirm('确定要删除这个存储桶吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          await this.$axios.delete(`/projects/${this.selectedProjectId}/buckets/${bucket.id}`)
          this.$message.success('存储桶删除成功')
          this.fetchBuckets()
        } catch (error) {
          this.$message.error('存储桶删除失败')
        }
      }).catch(() => {
        this.$message.info('已取消删除')
      })
    },
    viewBucket(bucket) {
      this.$router.push({
        path: `/space/${this.selectedProjectId}/storage/${bucket.id}/files`,
        query: { name: bucket.name }
      })
    },
    
    // 页码改变
    handlePageChange(page) {
      this.pagination.currentPage = page;
    },
    
    // 处理表格多选
    handleSelectionChange(selection) {
      this.selectedBuckets = selection;
    },
    
    // 批量删除存储桶
    async batchDelete() {
      if (this.selectedBuckets.length === 0) {
        this.$message.warning('请先选择要删除的存储桶');
        return;
      }

      try {
        await this.$confirm(`确定要删除选中的 ${this.selectedBuckets.length} 个存储桶吗？此操作不可恢复！`, '警告', {
          type: 'warning',
          confirmButtonText: '确定删除',
          cancelButtonText: '取消'
        });
        
        const ids = this.selectedBuckets.map(bucket => bucket.id);
        
        await this.$axios.post(`/projects/${this.selectedProjectId}/buckets/batch-delete`, {
          ids: ids
        });
        
        this.$message.success(`成功删除 ${ids.length} 个存储桶`);
        this.selectedBuckets = [];
        this.fetchBuckets();
      } catch (error) {
        if (error !== 'cancel') {
          console.error('批量删除失败:', error);
          this.$message.error('批量删除失败: ' + (error.response?.data?.error || error.message));
        }
      }
    }
  }
}
</script>

<style scoped>
.storage-buckets-container {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.storage-buckets-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-shrink: 0;
}

.storage-buckets-header h2 {
  margin: 0;
  color: #303133;
}

.project-selector-card {
  margin-bottom: 20px;
  flex-shrink: 0;
}

.project-selector {
  display: flex;
  align-items: center;
  gap: 10px;
}

.project-selector label {
  font-weight: 500;
  color: #606266;
}

.storage-buckets-card {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.storage-buckets-card >>> .el-card__body {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0;
}

.storage-buckets-card >>> .el-table {
  flex: 1;
  overflow-y: auto;
}

/* 分页固定在底部 */
.pagination-footer {
  padding: 16px;
  border-top: 1px solid #e4e7ed;
  background: white;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-shrink: 0;
  gap: 16px;
}

.footer-left {
  margin-right: auto;
}
</style>
